import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);

const DEFAULTS = {
  app_name: "Deuro",
  primary_color: "#1e3326",
  accent_color: "#d97706",
  iban: "",
  account_holder: "",
  features: { map: true, market: true, trades: true },
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function safeColor(value, fallback) {
  return HEX_COLOR.test(value || "") ? value.toLowerCase() : fallback;
}

function toRgb(hex) {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function mix(hex, target, amount) {
  const source = toRgb(hex);
  const destination = toRgb(target);
  const values = source.map((value, index) => Math.round(value + (destination[index] - value) * amount));
  return `#${values.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function onColor(hex) {
  const [r, g, b] = toRgb(hex).map((value) => value / 255);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.62 ? "#18211b" : "#ffffff";
}

function applyTheme(primary, accent) {
  primary = safeColor(primary, DEFAULTS.primary_color);
  accent = safeColor(accent, DEFAULTS.accent_color);
  const primaryRgb = toRgb(primary).join(",");
  const accentRgb = toRgb(accent).join(",");
  const root = document.documentElement;
  const variables = {
    "--primary": primary,
    "--primary-rgb": primaryRgb,
    "--primary-hover": mix(primary, "#ffffff", 0.12),
    "--primary-soft": mix(primary, "#ffffff", 0.88),
    "--on-primary": onColor(primary),
    "--accent": accent,
    "--gold": accent,
    "--accent-rgb": accentRgb,
    "--accent-hover": mix(accent, "#000000", 0.12),
    "--accent-soft": mix(accent, "#ffffff", 0.84),
    "--accent-border": mix(accent, "#ffffff", 0.58),
    "--accent-ink": mix(accent, "#111111", 0.55),
    "--on-accent": onColor(accent),
  };
  Object.entries(variables).forEach(([key, value]) => root.style.setProperty(key, value));
  let el = document.getElementById("deuro-theme");
  if (!el) {
    el = document.createElement("style");
    el.id = "deuro-theme";
    document.head.appendChild(el);
  }
  el.textContent = `
    [class~="bg-[#1e3326]"]{background-color:var(--primary)!important;color:var(--on-primary);}
    [class~="text-[#1e3326]"]{color:var(--primary)!important;}
    [class~="border-[#1e3326]"]{border-color:var(--primary)!important;}
    [class~="bg-[#d97706]"]{background-color:var(--accent)!important;color:var(--on-accent);}
    [class~="text-[#d97706]"],[class~="text-[#f59e0b]"]{color:var(--accent)!important;}
    [class~="border-[#d97706]"]{border-color:var(--accent)!important;}
    [class~="bg-[#e6ece7]"]{background-color:var(--primary-soft)!important;}
    [class~="bg-[#fef3c7]"]{background-color:var(--accent-soft)!important;}
    [class~="text-[#92600a]"]{color:var(--accent-ink)!important;}
    [class~="border-[#f3d98a]"]{border-color:var(--accent-border)!important;}
    [class~="hover:bg-[#284635]"]:hover{background-color:var(--primary-hover)!important;}
    [class~="hover:bg-[#c26806]"]:hover,[class~="hover:bg-[#f59e0b]"]:hover{background-color:var(--accent-hover)!important;}
    [class~="hover:bg-[#d5e0d8]"]:hover{background-color:var(--primary-soft)!important;filter:brightness(.95);}
    [class~="hover:bg-[#fde9a8]"]:hover{background-color:var(--accent-soft)!important;filter:brightness(.94);}
    [class~="hover:text-[#d97706]"]:hover{color:var(--accent)!important;}
    [class~="hover:border-[#d97706]"]:hover,[class~="hover:border-[#d97706]/50"]:hover{border-color:var(--accent)!important;}
    [class~="focus-visible:ring-[#d97706]"]:focus-visible{--tw-ring-color:var(--accent)!important;}
    .group:hover [class~="group-hover:bg-[#fef3c7]"]{background-color:var(--accent-soft)!important;}
    .group:hover [class~="group-hover:text-[#d97706]"]{color:var(--accent)!important;}
    .pin-privato{background:${primary} !important;}
    .pin-attivita{background:${accent} !important;}
  `;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULTS);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get(user ? "/settings" : "/settings/public");
      const merged = { ...DEFAULTS, ...data, features: { ...DEFAULTS.features, ...(data.features || {}) } };
      setSettings(merged);
      applyTheme(merged.primary_color, merged.accent_color);
      return merged;
    } catch {
      applyTheme(DEFAULTS.primary_color, DEFAULTS.accent_color);
      return DEFAULTS;
    }
  }, [user]);

  useEffect(() => {
    refreshSettings();
  }, [user, refreshSettings]);

  const previewTheme = useCallback((primary, accent) => applyTheme(primary, accent), []);

  return (
    <ThemeContext.Provider value={{ settings, setSettings, refreshSettings, previewTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
